import type { BaseResource, Dependencies, DepNode, DepTree, RuleLinks, RuleRef } from "../../shared/patterns.js";
import type { Stack } from "../../shared/stack.js";
import type { AppContext } from "../Page.js";
import { debounce, h, patch } from "../dom.js";
import { amount, stackName } from "../format.js";
import { stackChip } from "./PatternBits.js";

const SHOWN_RULES = 12;

/** Rules as clickable chips that open their tree. */
function ruleChips(rules: RuleRef[], open: (key: string) => void, limit = SHOWN_RULES): HTMLElement {
  const chip = (r: RuleRef) =>
    h("button", { type: "button", class: "chip-button", title: "Show its tree", onclick: () => open(r.key) }, stackChip(r.stack, null));
  return h(
    "div",
    { class: "chips" },
    ...rules.slice(0, limit).map(chip),
    rules.length > limit ? h("span", { class: "muted small" }, `and ${rules.length - limit} more`) : null,
  );
}

/**
 * The Dependencies tab of the Patterns page: a craft's whole ingredient tree,
 * the ingredients nothing makes that the rules rest on (auto-prod lines
 * first in mind), and which rules feed which.
 */
export class DependencyView {
  readonly el = h("div");
  private data: Dependencies | null = null;
  private tree: DepTree | null = null;
  private treeKey: string | null;

  private readonly search = h("input", { type: "search", placeholder: "Show the tree of… (anything a pattern makes)" });
  private readonly results = h("div", { class: "chips" });
  private readonly treeBox = h("div");
  private readonly baseBox = h("div");
  private readonly linksBox = h("div");
  private readonly baseFilter = h("select", null, h("option", { value: "" }, "All"), h("option", { value: "autoprod" }, "Auto-prod only"), h("option", { value: "missing" }, "Nothing makes it, not auto-prod"));

  constructor(
    private readonly ctx: AppContext,
    initialKey: string | null,
    private readonly onTree: (key: string | null) => void,
  ) {
    this.treeKey = initialKey;
    this.search.addEventListener("input", debounce(() => void this.searchOutputs(), 200));
    this.baseFilter.addEventListener("change", () => this.renderBase());
    this.el.append(
      h(
        "p",
        { class: "muted small" },
        "Follows the first pattern of every ingredient, as AE usually does. Green chips are autocraftable, blue are auto-prod, red are made by nothing.",
      ),
      h("div", { class: "card" }, h("h3", null, "Ingredient tree"), h("div", { class: "toolbar" }, this.search), this.results, this.treeBox),
      h("div", { class: "card" }, h("div", { class: "row" }, h("h3", null, "What your rules rest on"), h("span", { class: "spacer" }), this.baseFilter), this.baseBox),
      h("div", { class: "card" }, h("h3", null, "Rules that feed other rules"), this.linksBox),
    );
  }

  async load(): Promise<void> {
    try {
      this.data = await this.ctx.api.dependencies();
    } catch (err) {
      patch(this.baseBox, h("p", { class: "error" }, (err as Error).message));
      return;
    }
    this.renderBase();
    this.renderLinks();
    if (this.treeKey) await this.openTree(this.treeKey, false);
    else this.renderTree();
  }

  private async searchOutputs(): Promise<void> {
    const q = this.search.value.trim();
    if (!q) {
      patch(this.results);
      return;
    }
    let stacks: Stack[];
    try {
      stacks = await this.ctx.api.patternOutputs(q, 20);
    } catch (err) {
      patch(this.results, h("span", { class: "error small" }, (err as Error).message));
      return;
    }
    patch(this.results, 
      ...(stacks.length
        ? stacks.map((s) =>
            h("button", { type: "button", class: "chip-button", onclick: () => void this.openTree(stackKeyOf(s)) }, stackChip(s, null)),
          )
        : [h("span", { class: "muted small" }, "No pattern makes anything matching.")]),
    );
  }

  async openTree(key: string, scroll = true): Promise<void> {
    this.treeKey = key;
    this.onTree(key);
    patch(this.treeBox, h("p", { class: "muted" }, "Loading…"));
    try {
      this.tree = await this.ctx.api.dependencyTree(key);
    } catch (err) {
      this.tree = null;
      patch(this.treeBox, h("p", { class: "error" }, (err as Error).message));
      return;
    }
    this.renderTree();
    if (scroll) this.treeBox.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }

  private renderTree(): void {
    const t = this.tree;
    if (!t) {
      patch(this.treeBox, h("p", { class: "muted small" }, "Search above, or click a rule below, to see everything its craft takes."));
      return;
    }
    const open = (key: string) => void this.openTree(key);
    patch(this.treeBox, 
      t.root.patterns === 0
        ? h("p", { class: "muted" }, `No loaded pattern makes ${stackName(t.root.stack)}.`)
        : h("ul", { class: "dep-tree" }, this.node(t.root, true)),
      t.base.length
        ? h("div", null, h("div", { class: "small muted" }, "Bottom of the tree (nothing makes these):"), h("div", { class: "chips" }, ...t.base.map((b) => stackChip(b.stack, null, b.autoProd ? "autoprod" : "missing"))))
        : "",
      h(
        "div",
        null,
        h("div", { class: "small muted" }, t.usedBy.length ? `Needed by ${t.usedBy.length} rule${t.usedBy.length === 1 ? "" : "s"}:` : "No rule's craft needs it."),
        t.usedBy.length ? ruleChips(t.usedBy, open) : null,
      ),
    );
  }

  private node(n: DepNode, root = false): HTMLElement {
    const label = h(
      "span",
      { class: "dep-label" },
      stackChip(n.stack, n.amount, root ? undefined : n.source),
      n.stock !== null ? h("span", { class: "muted small" }, ` ${amount(n.stock, n.stack.type)} in stock`) : null,
      n.rule ? h("span", { class: "badge ok", title: "A rule keeps it stocked" }, "rule") : null,
      n.patterns > 1 ? h("span", { class: "muted small", title: "The tree follows the first one" }, ` · ${n.patterns} patterns`) : null,
      n.repeat ? h("span", { class: "muted small" }, " · expanded above") : null,
      n.cycle ? h("span", { class: "small warn" }, " · loops back") : null,
      n.truncated ? h("span", { class: "muted small" }, " · not followed further") : null,
    );
    if (!n.children.length) return h("li", null, label);
    return h("li", null, h("details", { open: root || n.children.length <= 6 }, h("summary", null, label), h("ul", null, ...n.children.map((c) => this.node(c)))));
  }

  private renderBase(): void {
    const data = this.data;
    if (!data?.status.count) {
      patch(this.baseBox, h("p", { class: "muted" }, "Load the patterns first."));
      return;
    }
    const filter = this.baseFilter.value;
    const items = data.base.filter((b) => !filter || (filter === "autoprod" ? b.autoProd : !b.autoProd));
    if (!items.length) {
      patch(this.baseBox, h("p", { class: "muted empty" }, data.base.length ? "Nothing matches." : "No rule depends on an ingredient nothing makes."));
      return;
    }
    const open = (key: string) => void this.openTree(key);
    const toggle = (b: BaseResource) =>
      h(
        "button",
        {
          class: `small${b.autoProd ? "" : " primary"}`,
          onclick: async () => {
            try {
              await this.ctx.api.setAutoProd(b.stack, !b.autoProd);
              await this.load();
            } catch (err) {
              this.ctx.toast((err as Error).message, "error");
            }
          },
        },
        b.autoProd ? "Unmark" : "Mark auto-prod",
      );
    patch(this.baseBox, 
      h(
        "p",
        { class: "muted small" },
        "Each of these is at the bottom of some rule's tree. If one runs dry, every rule listed against it stalls.",
      ),
      h(
        "div",
        { class: "table-wrap" },
        h(
          "table",
          null,
          h("thead", null, h("tr", null, h("th", null, "Ingredient"), h("th", { class: "num" }, "In stock"), h("th", { class: "num" }, "Rules"), h("th", null, "Needed by"), h("th", null, ""))),
          h(
            "tbody",
            null,
            ...items.map((b) =>
              h(
                "tr",
                null,
                h("td", null, stackChip(b.stack, null, b.autoProd ? "autoprod" : "missing")),
                h("td", { class: "num" }, amount(b.stock, b.stack.type)),
                h("td", { class: "num" }, String(b.rules.length)),
                h("td", null, ruleChips(b.rules, open, 6)),
                h("td", { class: "actions" }, toggle(b)),
              ),
            ),
          ),
        ),
      ),
    );
  }

  private renderLinks(): void {
    const links = this.data?.links ?? [];
    if (!this.data?.status.count) {
      patch(this.linksBox, h("p", { class: "muted" }, "Load the patterns first."));
      return;
    }
    if (!links.length) {
      patch(this.linksBox, h("p", { class: "muted empty" }, "No rule's stack is an ingredient of another rule."));
      return;
    }
    const open = (key: string) => void this.openTree(key);
    const row = (l: RuleLinks) =>
      h(
        "tr",
        null,
        h("td", null, h("button", { type: "button", class: "chip-button", title: "Show its tree", onclick: () => open(l.key) }, stackChip(l.stack, null))),
        h("td", null, l.feeds.length ? ruleChips(l.feeds, open, 6) : h("span", { class: "muted" }, "–")),
        h("td", null, l.fedBy.length ? ruleChips(l.fedBy, open, 6) : h("span", { class: "muted" }, "–")),
      );
    patch(this.linksBox, 
      h(
        "p",
        { class: "muted small" },
        "A rule feeds another when its stack is somewhere down the other's tree, so keeping it stocked lets the other craft start.",
      ),
      h(
        "div",
        { class: "table-wrap" },
        h(
          "table",
          null,
          h("thead", null, h("tr", null, h("th", null, "Rule"), h("th", null, "Feeds"), h("th", null, "Fed by"))),
          h("tbody", null, ...links.map(row)),
        ),
      ),
    );
  }
}

function stackKeyOf(s: Stack): string {
  return s.type === "item" ? `item:${s.name}:${s.damage ?? 0}` : `${s.type}:${s.name}`;
}
